"use client";

import { SUPPORTED_LANGUAGES } from "~/i18n/config";
import { useOptionalLocale, useTranslator } from "~/i18n/provider";
import { useMarketPicker } from "~/components/storefront/market/market-picker-context";
import { cn } from "~/lib/utils";

/**
 * P11-19 · The trigger — "EN · EUR · Europe".
 *
 * Pure trigger: it owns no state and no dialog. Three of these mount (header,
 * mobile menu, footer) against the ONE picker rendered at layout level.
 *
 * Each variant carries its own `data-testid`. Three elements sharing one id
 * would make every `getByTestId("market-pill")` in the suite a strict-mode
 * violation — and the mobile one is in the DOM even on desktop, hidden by CSS,
 * so "only one is visible" would not save it.
 */

function languageLabel(language: string): string {
  return (
    SUPPORTED_LANGUAGES[language as keyof typeof SUPPORTED_LANGUAGES]
      ?.endonym ?? language.toUpperCase()
  );
}

export function MarketPill({
  variant = "header",
  onOpen,
  className,
}: {
  variant?: "header" | "mobile" | "footer";
  /** Runs before the picker opens — the mobile menu passes its own dismiss. */
  onOpen?: () => void;
  className?: string;
}) {
  const locale = useOptionalLocale();
  const t = useTranslator();
  const picker = useMarketPicker();

  // No locale or no picker → nothing to trigger. Both are legitimate: the pill
  // is an optional ornament, so it renders nothing rather than throwing.
  if (!locale || !picker) return null;

  const languages = locale.enabledLanguages;
  const currencies = locale.marketCurrencies;
  const regions = locale.marketRegions;

  // Same rule as the picker: no plural axis, no control. Kept here as well as
  // there so a brand with nothing to offer renders no chrome at all rather
  // than a button that opens an empty panel.
  const plural =
    languages.length > 1 || currencies.length > 1 || regions.length > 1;
  if (!plural) return null;

  const currentRegion =
    regions.find((r) => r.code === (locale.region ?? "")) ?? regions[0];
  const currentRegionName =
    regions.length > 0 ? (currentRegion?.name ?? null) : null;

  const label = `${t("locale.trigger")}: ${languageLabel(locale.language)}${
    currencies.length > 1 ? `, ${locale.currency}` : ""
  }${currentRegionName ? `, ${t("locale.shipToLabel")} ${currentRegionName}` : ""}`;

  const summary = (
    <>
      {locale.language.toUpperCase()}
      {currencies.length > 1 ? ` · ${locale.currency}` : ""}
      {regions.length > 1 && currentRegionName ? ` · ${currentRegionName}` : ""}
    </>
  );

  return (
    <button
      type="button"
      onClick={() => picker.openPicker(onOpen)}
      aria-haspopup="dialog"
      aria-expanded={picker.open}
      data-testid={
        variant === "header" ? "market-pill" : `market-pill-${variant}`
      }
      // The header pill is icon-only below `sm`, so its accessible name has to
      // carry the full state; the other two always show their label, but the
      // same name costs nothing and keeps them consistent to a screen reader.
      aria-label={label}
      className={cn(
        "inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
        variant === "header" &&
          "rounded-md px-2 py-1.5 text-xs font-semibold text-fg hover:bg-surface-2",
        variant === "mobile" &&
          "w-full rounded-md px-2 py-2 text-sm text-fg hover:bg-surface-2",
        variant === "footer" &&
          "rounded-md text-xs text-on-surface-inverse-faint underline-offset-2 hover:text-on-surface-inverse hover:underline",
        className,
      )}
    >
      <GlobeIcon />
      <span className={variant === "header" ? "hidden sm:inline" : undefined}>
        {summary}
      </span>
    </button>
  );
}

function GlobeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4 flex-none"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
    </svg>
  );
}
